import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Sign a request using AWS Signature V4
 * This is needed for Lambda Function URLs with AWS_IAM auth
 */
export async function signedFetch(url, options = {}) {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    const { credentials, tokens } = session;

    if (!credentials) {
      throw new Error('No AWS credentials available. Please ensure you are logged in and Identity Pool is configured.');
    }

    // Parse the URL
    const urlObj = new URL(url);
    const region = 'us-east-2';
    const service = 'lambda';
    
    // Prepare the request
    const method = options.method || 'GET';
    const body = options.body || '';
    const headers = {
      ...options.headers,
      'Content-Type': 'application/json',
    };

    const accessToken = tokens?.accessToken?.toString();
    if (accessToken) {
      headers['X-Cognito-Access-Token'] = accessToken;
    }
    
    // Create the canonical request components
    const amzDate = new Date().toISOString().replace(/[:-]|\.\d{3}/g, '');
    const dateStamp = amzDate.substring(0, 8);
    
    headers['X-Amz-Date'] = amzDate;
    
    // If we have a session token, add it
    if (credentials.sessionToken) {
      headers['X-Amz-Security-Token'] = credentials.sessionToken;
    }

    const signingHeaders = {
      ...headers,
      'Host': urlObj.host,
    };
    
    // Sort headers for canonical form
    const sortedHeaders = Object.keys(signingHeaders)
      .sort()
      .map(key => key.toLowerCase() + ':' + signingHeaders[key])
      .join('\n');
    
    const signedHeaders = Object.keys(signingHeaders)
      .sort()
      .map(key => key.toLowerCase())
      .join(';');
    
    // Create canonical request
    const canonicalRequest = [
      method,
      urlObj.pathname,
      urlObj.search.substring(1), // Remove leading ?
      sortedHeaders,
      '',
      signedHeaders,
      await sha256(body)
    ].join('\n');
    
    // Create string to sign
    const algorithm = 'AWS4-HMAC-SHA256';
    const credentialScope = dateStamp + '/' + region + '/' + service + '/' + 'aws4_request';
    const stringToSign = [
      algorithm,
      amzDate,
      credentialScope,
      await sha256(canonicalRequest)
    ].join('\n');
    
    // Calculate signature
    const signingKey = await getSignatureKey(
      credentials.secretAccessKey,
      dateStamp,
      region,
      service
    );
    
    const signature = await hmacHex(signingKey, stringToSign);
    
    // Add authorization header
    headers['Authorization'] = algorithm + ' ' +
      'Credential=' + credentials.accessKeyId + '/' + credentialScope + ', ' +
      'SignedHeaders=' + signedHeaders + ', ' +
      'Signature=' + signature;
    
    // Make the request
    return fetch(url, {
      ...options,
      method,
      headers,
      body: body || undefined
    });
    
  } catch (error) {
    console.error('Error signing request:', error);
    throw error;
  }
}

// Helper functions for crypto operations
async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

async function hmac(key, message) {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, new TextEncoder().encode(message));
  return new Uint8Array(signature);
}

async function hmacHex(key, message) {
  const mac = await hmac(key, message);
  return Array.from(mac).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function getSignatureKey(secretKey, dateStamp, regionName, serviceName) {
  const kDate = await hmac(new TextEncoder().encode('AWS4' + secretKey), dateStamp);
  const kRegion = await hmac(kDate, regionName);
  const kService = await hmac(kRegion, serviceName);
  const kSigning = await hmac(kService, 'aws4_request');
  return kSigning;
}
